import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Timer, Package, Zap, Star, Percent } from "lucide-react";
import { AnimatedBackground } from "./AnimatedBackground";
import { StructuredData } from "./StructuredData";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";

interface TipBundle {
  id: string;
  title: string;
  description: string;
  bundle_type: string;
  tip_ids: string[];
  original_price: number;
  discounted_price: number;
  discount_percentage: number;
  expires_at: string;
  max_purchases: number;
  current_purchases: number;
}

interface TipBundlesProps {
  onPurchase?: (bundleId: string) => void;
}

export function TipBundles({ onPurchase }: TipBundlesProps) {
  const [bundles, setBundles] = useState<TipBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { trackPurchase, trackViewItem, trackAddToCart } = useAnalytics();

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('tip_bundles')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Filter valid bundles (not expired)
      const validBundles = (data || []).filter(bundle => {
        if (!bundle.expires_at) return true;
        return new Date(bundle.expires_at) > new Date();
      });
      
      setBundles(validBundles);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      // Don't show error toast, just set empty bundles gracefully
      setBundles([]);
    } finally {
      setLoading(false);
    }
  };

  const getBundleTypeIcon = (type: string) => {
    switch (type) {
      case 'sport': return '⚽';
      case 'ai': return '🤖';
      case 'premium': return '👑';
      case 'region': return '🌍';
      default: return '📦';
    }
  };

  const getBundleTypeColor = (type: string) => {
    switch (type) {
      case 'sport': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'ai': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'premium': return 'bg-gold-500/10 text-gold-600 border-gold-500/20';
      case 'region': return 'bg-green-500/10 text-green-600 border-green-500/20';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  const calculateTimeLeft = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const difference = expiry - now;

    if (difference <= 0) return "Expired";

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const handlePurchase = async (bundleId: string) => {
    const bundle = bundles.find(b => b.id === bundleId);
    
    if (bundle) {
      // Track add to cart event
      trackAddToCart({
        id: bundleId,
        name: bundle.title,
        price: bundle.discounted_price,
        category: bundle.bundle_type
      });
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { bundle_id: bundleId }
      });

      if (error) throw error;
      
      if (bundle) {
        trackPurchase(bundleId, bundle.discounted_price, bundle.title);
      }
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast({
        title: "Error",
        description: "Failed to initiate checkout",
        variant: "destructive",
      });
    }
    
    if (onPurchase) {
      onPurchase(bundleId);
    }
  };

  // Track bundle view when bundles load
  useEffect(() => {
    if (bundles.length > 0) {
      bundles.forEach(bundle => {
        trackViewItem({
          id: bundle.id,
          name: bundle.title,
          category: bundle.bundle_type,
          price: bundle.discounted_price
        });
      });
    }
  }, [bundles]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (bundles.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="text-center p-8 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent>
            <Package className="w-16 h-16 mx-auto mb-4 text-primary animate-pulse" />
            <h3 className="text-2xl font-bold mb-3">Coming Soon: Premium Bundles 🔥</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              We're curating exclusive tip bundles from our top performers. Be the first to know when they drop!
            </p>
            <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <Card className="p-4 border-dashed">
                <div className="text-3xl mb-2">⚽</div>
                <h4 className="font-semibold text-sm mb-1">Premier League Pack</h4>
                <p className="text-xs text-muted-foreground">Top EPL insights</p>
              </Card>
              <Card className="p-4 border-dashed">
                <div className="text-3xl mb-2">🤖</div>
                <h4 className="font-semibold text-sm mb-1">AI Value Bundle</h4>
                <p className="text-xs text-muted-foreground">AI-curated picks</p>
              </Card>
              <Card className="p-4 border-dashed">
                <div className="text-3xl mb-2">🏆</div>
                <h4 className="font-semibold text-sm mb-1">Champions Special</h4>
                <p className="text-xs text-muted-foreground">UCL predictions</p>
              </Card>
            </div>
            <Button className="mt-6" size="lg">
              <Zap className="h-4 w-4 mr-2" />
              Notify Me When Available
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AnimatedBackground variant="minimal">
      <div className="space-y-6">
        {bundles.map(bundle => (
          <StructuredData
            key={bundle.id}
            type="Product"
            data={{
              name: bundle.title,
              description: bundle.description,
              brand: "BetVerse",
              price: bundle.discounted_price,
              priceCurrency: "USD",
              availability: bundle.current_purchases < bundle.max_purchases ? "InStock" : "OutOfStock",
              url: `https://betverse-global-picks.lovable.app/bundles/${bundle.id}`
            }}
          />
        ))}
        
        <div>
          <h2 className="text-2xl font-bold mb-2">🔥 Hot Tip Bundles</h2>
          <p className="text-muted-foreground">Curated collections of premium tips at discounted prices</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bundles.map((bundle) => {
            const purchaseProgress = (bundle.current_purchases / bundle.max_purchases) * 100;
            const timeLeft = calculateTimeLeft(bundle.expires_at);
            const isExpired = timeLeft === "Expired";
            
            return (
              <Card key={bundle.id} className="relative overflow-hidden hover:shadow-elegant transition-all duration-300 hover-scale group">
                <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-5 transition-opacity duration-300" />
                
                {bundle.discount_percentage > 0 && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge variant="destructive" className="text-xs font-bold">
                      -{bundle.discount_percentage}%
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {getBundleTypeIcon(bundle.bundle_type)}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg leading-tight">{bundle.title}</CardTitle>
                      <Badge className={`mt-2 text-xs ${getBundleTypeColor(bundle.bundle_type)}`}>
                        {bundle.bundle_type.charAt(0).toUpperCase() + bundle.bundle_type.slice(1)} Bundle
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {bundle.description}
                  </p>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tips included:</span>
                    <span className="font-medium">{bundle.tip_ids.length}</span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Availability:</span>
                      <span className="font-medium">
                        {bundle.max_purchases - bundle.current_purchases} left
                      </span>
                    </div>
                    <Progress value={purchaseProgress} className="h-1.5" />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Timer className="w-3 h-3" />
                    <span className={isExpired ? "text-destructive" : "text-warning"}>
                      {isExpired ? "Expired" : `${timeLeft} remaining`}
                    </span>
                  </div>

                  <div className="pt-2 border-t border-border/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="space-y-1">
                        {bundle.discount_percentage > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground line-through">
                              ${bundle.original_price}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              <Percent className="w-2 h-2 mr-1" />
                              Save ${(bundle.original_price - bundle.discounted_price).toFixed(2)}
                            </Badge>
                          </div>
                        )}
                        <div className="text-xl font-bold text-profit">
                          ${bundle.discounted_price}
                        </div>
                      </div>

                      <Button
                        variant={bundle.bundle_type === 'premium' ? 'premium' : 'default'}
                        size="sm"
                        onClick={() => handlePurchase(bundle.id)}
                        disabled={isExpired || bundle.current_purchases >= bundle.max_purchases}
                        className="flex items-center gap-1"
                      >
                        {isExpired ? (
                          "Expired"
                        ) : bundle.current_purchases >= bundle.max_purchases ? (
                          "Sold Out"
                        ) : (
                          <>
                            <Zap className="w-3 h-3" />
                            Buy Bundle
                          </>
                        )}
                      </Button>
                    </div>

                    {bundle.bundle_type === 'ai' && (
                      <div className="flex items-center gap-1 text-xs text-purple-600">
                        <Star className="w-3 h-3" />
                        AI-curated predictions
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AnimatedBackground>
  );
}